import { create } from 'zustand'
import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL         || 'http://localhost:8000'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
const USER_STORAGE_KEY = 'auth_user'

// ─── shared axios helper ──────────────────────────────────────────────────────
const authAxios = axios.create()
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(JWT_STORAGE_KEY)
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

// ─── store ────────────────────────────────────────────────────────────────────
const useAuthStore = create((set, get) => ({
  user:            localStorage.getItem(USER_STORAGE_KEY) ? JSON.parse(localStorage.getItem(USER_STORAGE_KEY)) : null,
  token:           localStorage.getItem(JWT_STORAGE_KEY),
  isAuthenticated: !!localStorage.getItem(JWT_STORAGE_KEY),
  isLoading:       false,
  error:           null,

  // Holds 2FA challenge state while waiting for the code:
  //   { temp_token, method: 'totp' | 'email', sent_to?: 'a***@x.com', resend_after?: seconds }
  pendingTwoFactor: null,

  // ── login (step 1) ──────────────────────────────────────────────────────────
  // authMethod: 'totp' (default — Authenticator app) or 'email' (send code via email)
  login: async (username, password, authMethod = 'totp') => {
    set({ isLoading: true, error: null, pendingTwoFactor: null })
    try {
      const response = await axios.post(`${API_URL}/api/login/`, {
        username,
        password,
        auth_method: authMethod,
      })
      const data = response.data

      // Backend signals 2FA is required
      if (data.require_2fa) {
        set({
          isLoading: false,
          pendingTwoFactor: {
            temp_token:   data.temp_token,
            method:       data.method || 'totp',
            sent_to:      data.sent_to || null,
            resend_after: data.resend_after || 0,
          },
        })
        return {
          success: true,
          require_2fa: true,
          method:  data.method || 'totp',
          sent_to: data.sent_to || null,
          resend_after: data.resend_after || 0,
        }
      }

      // Normal login — store tokens
      const { access_token, refresh_token, user } = data
      localStorage.setItem(JWT_STORAGE_KEY, access_token)
      localStorage.setItem(import.meta.env.VITE_REFRESH_TOKEN_KEY, refresh_token)
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
      set({ user, token: access_token, isAuthenticated: true, isLoading: false })
      return { success: true, require_2fa: false }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || data?.detail || (typeof data === 'string' ? data : null) || 'Login failed'
      set({ error: message, isLoading: false })
      return { success: false, error: message }
    }
  },

  // ── verify 2FA (step 2) ─────────────────────────────────────────────────────
  // method defaults to whatever was set in pendingTwoFactor (so callers don't
  // have to pass it twice). Pass explicitly if you need to override.
  verifyTwoFactor: async (tempToken, code, method) => {
    const state = get()
    const resolvedMethod = method || state.pendingTwoFactor?.method || 'totp'
    set({ isLoading: true, error: null })
    try {
      const response = await axios.post(`${API_URL}/api/2fa/verify/`, {
        temp_token: tempToken,
        totp_code:  code,
        method:     resolvedMethod,
      })
      const { access_token, refresh_token, user } = response.data
      localStorage.setItem(JWT_STORAGE_KEY, access_token)
      localStorage.setItem(import.meta.env.VITE_REFRESH_TOKEN_KEY, refresh_token)
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
      set({ user, token: access_token, isAuthenticated: true, isLoading: false, pendingTwoFactor: null })
      return { success: true }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || data?.detail || 'Invalid code. Please try again.'
      set({ error: message, isLoading: false })
      return { success: false, error: message, reason: data?.reason }
    }
  },

  // ── resend email 2FA code ──────────────────────────────────────────────────
  resendEmail2faCode: async (tempToken) => {
    try {
      const response = await axios.post(`${API_URL}/api/2fa/email/resend/`, {
        temp_token: tempToken,
      })
      return { success: true, message: response.data.message, resend_after: response.data.resend_after }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || 'Could not send code. Try again later.'
      return {
        success: false,
        error: message,
        retry_after: data?.retry_after || 0,
        status: error.response?.status,
      }
    }
  },

  // ── logout ──────────────────────────────────────────────────────────────────
  logout: () => {
    localStorage.removeItem(JWT_STORAGE_KEY)
    localStorage.removeItem(import.meta.env.VITE_REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
    set({ user: null, token: null, isAuthenticated: false, pendingTwoFactor: null })
  },

  fetchCurrentUser: async () => {
    const stored = localStorage.getItem(USER_STORAGE_KEY)
    if (stored) {
      const user = JSON.parse(stored)
      set({ user })
      return user
    }
    return null
  },

  updateUser: (userData) => {
    set({ user: userData })
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData))
  },

  clearError: () => set({ error: null }),

  // ── 2FA account management API helpers ─────────────────────────────────────
  twoFactorApi: {
    getStatus: () =>
      authAxios.get(`${API_URL}/api/account/2fa/status/`),
    setup: () =>
      authAxios.post(`${API_URL}/api/account/2fa/setup/`),
    enable: (totp_code) =>
      authAxios.post(`${API_URL}/api/account/2fa/enable/`, { totp_code }),
    disable: (totp_code) =>
      authAxios.post(`${API_URL}/api/account/2fa/disable/`, { totp_code }),
    getBackupCodes: () =>
      authAxios.get(`${API_URL}/api/account/2fa/backup-codes/`),
    setupFromToken: (token) =>
      axios.get(`${API_URL}/api/account/2fa/setup-from-token/`, { params: { token } }),
    enableFromToken: (token, totp_code) =>
      axios.post(`${API_URL}/api/account/2fa/enable-from-token/`, { token, totp_code }),
    resendSetupEmail: () =>
      authAxios.post(`${API_URL}/api/account/2fa/resend-setup-email/`),
    adminGetUserStatus: (email) =>
      authAxios.get(`${API_URL}/api/admin/2fa/user-status/`, { params: { email } }),
    adminDisableUser: (user_email) =>
      authAxios.post(`${API_URL}/api/admin/2fa/disable-user/`, { user_email }),
  },
}))

export default useAuthStore
